// GENERATED FILE -- DO NOT EDIT.
// Source: config/crm-vocabulary.json
// Regenerate: make vocabulary-gen

export interface VocabularyTerm {
  readonly value: string;
  readonly label: string;
  readonly order: number;
  readonly aliases: readonly string[];
  /** Pipeline stages only: whether the stage represents an established relationship. */
  readonly warm?: boolean;
  /** Note statuses only: swatch colour used by the notes UI. */
  readonly color?: string;
  /** Plays only: the relationship motions this play belongs to. */
  readonly motions?: readonly string[];
}

/** Maps every canonical value and retired alias (lowercased) to its canonical value. */
function buildIndex(
  terms: readonly VocabularyTerm[],
): ReadonlyMap<string, string> {
  const index = new Map<string, string>();
  for (const term of terms) {
    index.set(term.value.trim().toLowerCase(), term.value);
    for (const alias of term.aliases) {
      index.set(alias.trim().toLowerCase(), term.value);
    }
  }
  return index;
}

/** Relationship pipeline stage for a contact or organization. Drives the Kanban board columns. Column: public.contacts.connection_pipeline_stage, public.companies.connection_pipeline_stage */
export const CONNECTION_PIPELINE_STAGES: readonly VocabularyTerm[] = [
  {
    value: "Prospect/Signal Queue",
    label: "Prospect/Signal Queue",
    order: 0,
    warm: false,
    aliases: [],
  },
  {
    value: "Identify Contact",
    label: "Identify Contact",
    order: 1,
    warm: false,
    aliases: [],
  },
  {
    value: "Engage",
    label: "Engage",
    order: 2,
    warm: false,
    aliases: ["Comment/React"],
  },
  {
    value: "Connecting",
    label: "Connecting",
    order: 3,
    warm: false,
    aliases: ["Connect Sent", "Connected"],
  },
  { value: "Outreach", label: "Outreach", order: 4, warm: false, aliases: [] },
  {
    value: "Meaningful exchange",
    label: "Meaningful exchange",
    order: 5,
    warm: true,
    aliases: ["Meaningful DM Exchange"],
  },
  {
    value: "Conversations",
    label: "Conversations",
    order: 6,
    warm: true,
    aliases: ["Conversations__", "Conversation Held"],
  },
  {
    value: "Explore Opportunity",
    label: "Explore Opportunity",
    order: 7,
    warm: true,
    aliases: [],
  },
  {
    value: "Qualified Lead",
    label: "Qualified Lead",
    order: 8,
    warm: true,
    aliases: [],
  },
  {
    value: "Active Relationship",
    label: "Active Relationship",
    order: 9,
    warm: true,
    aliases: [],
  },
  {
    value: "Dormant",
    label: "Dormant",
    order: 10,
    warm: false,
    aliases: ["Paused", "Zombie / Lost"],
  },
  {
    value: "Disqualified",
    label: "Disqualified",
    order: 11,
    warm: false,
    aliases: [],
  },
  { value: "Archived", label: "Archived", order: 12, warm: false, aliases: [] },
];

export const CONNECTION_PIPELINE_STAGE_DEFAULT = "Prospect/Signal Queue";

const CONNECTION_PIPELINE_STAGE_INDEX = buildIndex(CONNECTION_PIPELINE_STAGES);

/** Canonical connection_pipeline_stage values, in display order. */
export const CONNECTION_PIPELINE_STAGE_VALUES: readonly string[] =
  CONNECTION_PIPELINE_STAGES.map((term) => term.value);

/**
 * Resolve a connection_pipeline_stage value or retired alias to its canonical value.
 * Returns null when the input matches no known term.
 */
export function canonicalConnectionPipelineStage(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return (
    CONNECTION_PIPELINE_STAGE_INDEX.get(value.trim().toLowerCase()) ?? null
  );
}

/** Doctrine motion. Every record belongs to exactly one: Inbound (hand raised), Ecosystem (owns an audience; only place cold is allowed), Nurture (buyers/buyer-adjacent; cold forbidden), or a standing role. None means unrouted, which the doctrine treats as a defect. Column: public.contacts.relationship_motion, public.companies.relationship_motion */
export const RELATIONSHIP_MOTIONS: readonly VocabularyTerm[] = [
  { value: "None", label: "None", order: 0, aliases: [] },
  { value: "Inbound", label: "Inbound", order: 1, aliases: [] },
  { value: "Referral Source", label: "Referral Source", order: 2, aliases: [] },
  { value: "Ecosystem", label: "Ecosystem", order: 3, aliases: [] },
  {
    value: "Nurture",
    label: "Nurture",
    order: 4,
    aliases: ["Warm outreach", "Active Sequence", "Keep In Touch"],
  },
  {
    value: "Conversation Outreach",
    label: "Conversation Outreach",
    order: 5,
    aliases: ["Insight Outreach", "Insight Nurture"],
  },
  { value: "Customer", label: "Customer", order: 6, aliases: [] },
  { value: "Student", label: "Student", order: 7, aliases: [] },
  { value: "Vendor", label: "Vendor", order: 8, aliases: [] },
  { value: "Hold", label: "Hold", order: 9, aliases: [] },
];

export const RELATIONSHIP_MOTION_DEFAULT = "None";

const RELATIONSHIP_MOTION_INDEX = buildIndex(RELATIONSHIP_MOTIONS);

/** Canonical relationship_motion values, in display order. */
export const RELATIONSHIP_MOTION_VALUES: readonly string[] =
  RELATIONSHIP_MOTIONS.map((term) => term.value);

/**
 * Resolve a relationship_motion value or retired alias to its canonical value.
 * Returns null when the input matches no known term.
 */
export function canonicalRelationshipMotion(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return RELATIONSHIP_MOTION_INDEX.get(value.trim().toLowerCase()) ?? null;
}

/** Follow-up state for the next planned touch. Column: public.contacts.next_touch_status, public.companies.next_touch_status */
export const NEXT_TOUCH_STATUSES: readonly VocabularyTerm[] = [
  { value: "No Plan", label: "No Plan", order: 0, aliases: [] },
  { value: "Scheduled", label: "Scheduled", order: 1, aliases: [] },
  {
    value: "Requires Action",
    label: "Requires Action",
    order: 2,
    aliases: ["Due now"],
  },
  { value: "Waiting", label: "Waiting", order: 3, aliases: [] },
  { value: "On Hold", label: "On Hold", order: 4, aliases: [] },
  { value: "Done", label: "Done", order: 5, aliases: [] },
];

export const NEXT_TOUCH_STATUS_DEFAULT = "No Plan";

const NEXT_TOUCH_STATUS_INDEX = buildIndex(NEXT_TOUCH_STATUSES);

/** Canonical next_touch_status values, in display order. */
export const NEXT_TOUCH_STATUS_VALUES: readonly string[] =
  NEXT_TOUCH_STATUSES.map((term) => term.value);

/**
 * Resolve a next_touch_status value or retired alias to its canonical value.
 * Returns null when the input matches no known term.
 */
export function canonicalNextTouchStatus(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return NEXT_TOUCH_STATUS_INDEX.get(value.trim().toLowerCase()) ?? null;
}

/** Relationship-strength label on a contact note (David A. Fields A/B/C scale). The legacy atomic-crm cold/warm/hot scale is retained as aliases. Not DB-enforced: historic rows predate the scale. Column: public.contact_notes.status */
export const NOTE_STATUSES: readonly VocabularyTerm[] = [
  {
    value: "C",
    label: "C - Weak (Peripheral/Unknown)",
    order: 0,
    color: "#7dbde8",
    aliases: ["cold"],
  },
  {
    value: "B",
    label: "B - Medium (Recognize name)",
    order: 1,
    color: "#e8cb7d",
    aliases: ["warm"],
  },
  {
    value: "A",
    label: "A - Strong (Pick up phone)",
    order: 2,
    color: "#4ade80",
    aliases: ["hot"],
  },
];

export const NOTE_STATUS_DEFAULT = "C";

const NOTE_STATUS_INDEX = buildIndex(NOTE_STATUSES);

/** Canonical note_status values, in display order. */
export const NOTE_STATUS_VALUES: readonly string[] = NOTE_STATUSES.map(
  (term) => term.value,
);

/**
 * Resolve a note_status value or retired alias to its canonical value.
 * Returns null when the input matches no known term.
 */
export function canonicalNoteStatus(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return NOTE_STATUS_INDEX.get(value.trim().toLowerCase()) ?? null;
}

/** How well a contact knows Yuval — the David A. Fields A/B/C scale, plus 'None' for someone he has never actually interacted with. Distinct from note_status, which carries the same letters on contact_notes.status: a note's strength label and a person's relationship level are different facts, and adding 'None' to the shared kind would have put it in the note status picker. Not DB-enforced; historic rows predate the scale. Column: public.contacts.status */
export const RELATIONSHIP_LEVELS: readonly VocabularyTerm[] = [
  { value: "None", label: "None - no relationship", order: 0, aliases: [] },
  { value: "C", label: "C - Weak (Peripheral/Unknown)", order: 1, aliases: [] },
  { value: "B", label: "B - Medium (Recognize name)", order: 2, aliases: [] },
  { value: "A", label: "A - Strong (Pick up phone)", order: 3, aliases: [] },
];

export const RELATIONSHIP_LEVEL_DEFAULT = "C";

const RELATIONSHIP_LEVEL_INDEX = buildIndex(RELATIONSHIP_LEVELS);

/** Canonical relationship_level values, in display order. */
export const RELATIONSHIP_LEVEL_VALUES: readonly string[] =
  RELATIONSHIP_LEVELS.map((term) => term.value);

/**
 * Resolve a relationship_level value or retired alias to its canonical value.
 * Returns null when the input matches no known term.
 */
export function canonicalRelationshipLevel(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return RELATIONSHIP_LEVEL_INDEX.get(value.trim().toLowerCase()) ?? null;
}

/** Deal source attribution per the doctrine. There is deliberately no Cold value — if a deal ever needs one, the doctrine has been broken. NULL allowed for pre-doctrine legacy deals only. Column: public.deals.category */
export const DEAL_CATEGORIES: readonly VocabularyTerm[] = [
  { value: "Referral", label: "Referral", order: 0, aliases: [] },
  {
    value: "Repeat/Expansion",
    label: "Repeat/Expansion",
    order: 1,
    aliases: [],
  },
  {
    value: "Ecosystem/Channel",
    label: "Ecosystem/Channel",
    order: 2,
    aliases: [],
  },
  { value: "Content/Inbound", label: "Content/Inbound", order: 3, aliases: [] },
  { value: "Network/Warm", label: "Network/Warm", order: 4, aliases: [] },
];

export const DEAL_CATEGORY_DEFAULT = null;

const DEAL_CATEGORY_INDEX = buildIndex(DEAL_CATEGORIES);

/** Canonical deal_category values, in display order. */
export const DEAL_CATEGORY_VALUES: readonly string[] = DEAL_CATEGORIES.map(
  (term) => term.value,
);

/**
 * Resolve a deal_category value or retired alias to its canonical value.
 * Returns null when the input matches no known term.
 */
export function canonicalDealCategory(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return DEAL_CATEGORY_INDEX.get(value.trim().toLowerCase()) ?? null;
}

/** Advisory channel-type labels for ecosystem and referral records. Not DB-enforced: legacy multi-value semicolon groups remain valid. Column: public.contacts."group", public.companies."group" */
export const CONTACT_GROUPS: readonly VocabularyTerm[] = [
  {
    value: "Ecosystem - Podcast",
    label: "Ecosystem - Podcast",
    order: 0,
    aliases: [],
  },
  {
    value: "Ecosystem - Community",
    label: "Ecosystem - Community",
    order: 1,
    aliases: ["eco:community-session"],
  },
  {
    value: "Ecosystem - Event",
    label: "Ecosystem - Event",
    order: 2,
    aliases: [],
  },
  {
    value: "Ecosystem - Publication",
    label: "Ecosystem - Publication",
    order: 3,
    aliases: [],
  },
  {
    value: "Ecosystem - Vendor",
    label: "Ecosystem - Vendor",
    order: 4,
    aliases: ["eco:co-marketing"],
  },
  {
    value: "Ecosystem - Partner",
    label: "Ecosystem - Partner",
    order: 5,
    aliases: ["eco:white-label", "eco:co-delivery", "eco:platform-channel"],
  },
  {
    value: "Ecosystem - Influencer",
    label: "Ecosystem - Influencer",
    order: 6,
    aliases: [],
  },
  {
    value: "Ecosystem - Colleague",
    label: "Ecosystem - Colleague",
    order: 7,
    aliases: [],
  },
  { value: "Referral Source", label: "Referral Source", order: 8, aliases: [] },
];

export const CONTACT_GROUP_DEFAULT = null;

const CONTACT_GROUP_INDEX = buildIndex(CONTACT_GROUPS);

/** Canonical contact_group values, in display order. */
export const CONTACT_GROUP_VALUES: readonly string[] = CONTACT_GROUPS.map(
  (term) => term.value,
);

/**
 * Resolve a contact_group value or retired alias to its canonical value.
 * Returns null when the input matches no known term.
 */
export function canonicalContactGroup(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return CONTACT_GROUP_INDEX.get(value.trim().toLowerCase()) ?? null;
}

/** The suggested next move on this relationship and the script behind it — set by research or by Yuval's manual decision. Not all plays are outreach: Check-in and Relationship Restarter maintain an existing relationship, The Turn converts one, and Referral Ask is aimed at a third party. The motion picks the shortlist; the play picks the variant. Each term carries the motions it belongs to. Column: public.contacts.suggested_play, public.companies.suggested_play */
export const RELATIONSHIP_PLAYS: readonly VocabularyTerm[] = [
  {
    value: "Insight Note",
    label: "Insight Note",
    order: 0,
    motions: ["Conversation Outreach"],
    aliases: [],
  },
  {
    value: "Podcast Invite",
    label: "Podcast Invite",
    order: 1,
    motions: ["Conversation Outreach"],
    aliases: [],
  },
  {
    value: "Research Ask",
    label: "Research Ask",
    order: 2,
    motions: ["Conversation Outreach"],
    aliases: [],
  },
  {
    value: "Warm Intro Request",
    label: "Warm Intro Request",
    order: 3,
    motions: ["Conversation Outreach"],
    aliases: [],
  },
  {
    value: "Relationship Restarter",
    label: "Relationship Restarter",
    order: 4,
    motions: ["Nurture"],
    aliases: [],
  },
  {
    value: "Check-in",
    label: "Check-in",
    order: 5,
    motions: ["Nurture"],
    aliases: [],
  },
  {
    value: "The Turn",
    label: "The Turn",
    order: 6,
    motions: ["Nurture", "Conversation Outreach", "Ecosystem", "Customer"],
    aliases: [],
  },
  {
    value: "Referral Ask",
    label: "Referral Ask",
    order: 7,
    motions: ["Referral Source", "Customer"],
    aliases: [],
  },
  {
    value: "Podcast Guesting Pitch",
    label: "Podcast Guesting Pitch",
    order: 8,
    motions: ["Ecosystem"],
    aliases: [],
  },
  {
    value: "Community or Event Pitch",
    label: "Community or Event Pitch",
    order: 9,
    motions: ["Ecosystem"],
    aliases: [],
  },
  {
    value: "Channel Partner Outreach",
    label: "Channel Partner Outreach",
    order: 10,
    motions: ["Ecosystem"],
    aliases: [],
  },
  {
    value: "Find The Human",
    label: "Find The Human",
    order: 11,
    motions: ["Ecosystem"],
    aliases: [],
  },
];

export const RELATIONSHIP_PLAY_DEFAULT = null;

const RELATIONSHIP_PLAY_INDEX = buildIndex(RELATIONSHIP_PLAYS);

/** Canonical relationship_play values, in display order. */
export const RELATIONSHIP_PLAY_VALUES: readonly string[] =
  RELATIONSHIP_PLAYS.map((term) => term.value);

/**
 * Resolve a relationship_play value or retired alias to its canonical value.
 * Returns null when the input matches no known term.
 */
export function canonicalRelationshipPlay(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return RELATIONSHIP_PLAY_INDEX.get(value.trim().toLowerCase()) ?? null;
}

/** Role-agnostic strength: how good this record is at whatever role it plays, not how good a buyer it is. A bullseye delivery partner and a bullseye client are both `bullseye`; which role is which is what `relationship_types` says. That separation is the point of the two-axis model — the single-valued `icp_fit` it replaces could not describe a genuine co-delivery partner who will never buy. Judged at the organization and denormalised onto its people. NULL means the question has not been asked, which is what puts a record into the fit triage deck. Column: public.contacts.fit_quality, public.companies.fit_quality */
export const FIT_QUALITIES: readonly VocabularyTerm[] = [
  { value: "bullseye", label: "Bullseye", order: 0, aliases: [] },
  { value: "target", label: "Target", order: 1, aliases: [] },
  { value: "stretch", label: "Stretch", order: 2, aliases: [] },
  { value: "avoid", label: "Avoid", order: 3, aliases: [] },
  { value: "unknown", label: "Unknown", order: 4, aliases: [] },
];

export const FIT_QUALITY_DEFAULT = null;

const FIT_QUALITY_INDEX = buildIndex(FIT_QUALITIES);

/** Canonical fit_quality values, in display order. */
export const FIT_QUALITY_VALUES: readonly string[] = FIT_QUALITIES.map(
  (term) => term.value,
);

/**
 * Resolve a fit_quality value or retired alias to its canonical value.
 * Returns null when the input matches no known term.
 */
export function canonicalFitQuality(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return FIT_QUALITY_INDEX.get(value.trim().toLowerCase()) ?? null;
}

/** Which buyer profile a contact fits, populated when `relationship_types` includes `client`. About profile fit, not current status: whether someone IS currently a client lives in `lifecycle_stage` and `connection_pipeline_stage`. NULL when the relationship is not client-shaped at all, which is the normal case for a partner or an audience owner. Column: public.contacts.icp_profile */
export const ICP_PROFILES: readonly VocabularyTerm[] = [
  {
    value: "product/tech leader",
    label: "Product / tech leader",
    order: 0,
    aliases: [],
  },
  {
    value: "ai enablement/strategy leader",
    label: "AI enablement / strategy leader",
    order: 1,
    aliases: ["AI enablement leader with Agile background"],
  },
  { value: "agility leader", label: "Agility leader", order: 2, aliases: [] },
  { value: "business leader", label: "Business leader", order: 3, aliases: [] },
  { value: "ICP", label: "ICP - profile unclear", order: 4, aliases: [] },
];

export const ICP_PROFILE_DEFAULT = null;

const ICP_PROFILE_INDEX = buildIndex(ICP_PROFILES);

/** Canonical icp_profile values, in display order. */
export const ICP_PROFILE_VALUES: readonly string[] = ICP_PROFILES.map(
  (term) => term.value,
);

/**
 * Resolve a icp_profile value or retired alias to its canonical value.
 * Returns null when the input matches no known term.
 */
export function canonicalIcpProfile(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return ICP_PROFILE_INDEX.get(value.trim().toLowerCase()) ?? null;
}

/** What role this relationship plays. An ARRAY, which is the reason this issue exists: a delivery partner who is also a referral source is both, and the single-valued `ecosystem_fit` this replaces had to flatten that to one. Strength for every role is carried by `fit_quality`, which is role-agnostic, so there is no per-type tier. NULL means the question has never been asked; an empty array means it was asked and the answer is no role — the old `ecosystem_fit = 'None'`, preserved as a decision rather than an absence. Column: public.contacts.relationship_types (array) */
export const RELATIONSHIP_TYPES: readonly VocabularyTerm[] = [
  { value: "client", label: "Client", order: 0, aliases: [] },
  { value: "tool partner", label: "Tool partner", order: 1, aliases: [] },
  {
    value: "delivery partner",
    label: "Delivery partner",
    order: 2,
    aliases: ["Channel Partner"],
  },
  {
    value: "audience owner",
    label: "Audience owner",
    order: 3,
    aliases: ["Audience Owner"],
  },
  {
    value: "referral partner",
    label: "Referral partner",
    order: 4,
    aliases: [],
  },
  { value: "partner", label: "Partner - kind unclear", order: 5, aliases: [] },
];

export const RELATIONSHIP_TYPE_DEFAULT = null;

const RELATIONSHIP_TYPE_INDEX = buildIndex(RELATIONSHIP_TYPES);

/** Canonical relationship_type values, in display order. */
export const RELATIONSHIP_TYPE_VALUES: readonly string[] =
  RELATIONSHIP_TYPES.map((term) => term.value);

/**
 * Resolve a relationship_type value or retired alias to its canonical value.
 * Returns null when the input matches no known term.
 */
export function canonicalRelationshipType(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return RELATIONSHIP_TYPE_INDEX.get(value.trim().toLowerCase()) ?? null;
}

/** Where this person sits relative to a buying decision inside their own organization. One of the two inputs to the derived `buyer_proximity`; the other is `fit_quality`. Supersedes `contacts.influence`, which accumulated four incompatible encodings (Fields' 1/2/3, a High/Medium scale, an ICP profile, and free prose) across 425 rows and could not be filtered on. Column: public.contacts.buyer_status */
export const BUYER_STATUSES: readonly VocabularyTerm[] = [
  { value: "decision maker", label: "Decision maker", order: 0, aliases: [] },
  { value: "influencer", label: "Influencer", order: 1, aliases: [] },
  { value: "everyone else", label: "Everyone else", order: 2, aliases: [] },
];

export const BUYER_STATUS_DEFAULT = null;

const BUYER_STATUS_INDEX = buildIndex(BUYER_STATUSES);

/** Canonical buyer_status values, in display order. */
export const BUYER_STATUS_VALUES: readonly string[] = BUYER_STATUSES.map(
  (term) => term.value,
);

/**
 * Resolve a buyer_status value or retired alias to its canonical value.
 * Returns null when the input matches no known term.
 */
export function canonicalBuyerStatus(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return BUYER_STATUS_INDEX.get(value.trim().toLowerCase()) ?? null;
}

/** How good the evidence behind the fit judgement is. Measures EVIDENCE QUALITY, not enthusiasm — a `bullseye` at `low` confidence is a research task, not a target. Pairs with `fit_rationale` (the decisive facts) and `fit_missing_evidence` (the one fact that would change the call). Column: public.contacts.fit_confidence, public.companies.fit_confidence */
export const FIT_CONFIDENCES: readonly VocabularyTerm[] = [
  { value: "high", label: "High", order: 0, aliases: [] },
  { value: "medium", label: "Medium", order: 1, aliases: [] },
  { value: "low", label: "Low", order: 2, aliases: [] },
];

export const FIT_CONFIDENCE_DEFAULT = null;

const FIT_CONFIDENCE_INDEX = buildIndex(FIT_CONFIDENCES);

/** Canonical fit_confidence values, in display order. */
export const FIT_CONFIDENCE_VALUES: readonly string[] = FIT_CONFIDENCES.map(
  (term) => term.value,
);

/**
 * Resolve a fit_confidence value or retired alias to its canonical value.
 * Returns null when the input matches no known term.
 */
export function canonicalFitConfidence(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return FIT_CONFIDENCE_INDEX.get(value.trim().toLowerCase()) ?? null;
}

/** Headcount band, in the doctrine's bands. Deliberately not `companies.size`, which is Atomic CRM's smallint and NULL on all 5,320 rows; a text band is what the enrichment actually carries. `unknown` is NEGATIVE evidence for ICP rather than neutral: a 'CTO' at a company nobody can size is usually a micro-startup or a solo consultancy with an inflated title. Column: public.companies.size_band */
export const COMPANY_SIZE_BANDS: readonly VocabularyTerm[] = [
  { value: "micro", label: "Micro (<10)", order: 0, aliases: [] },
  { value: "small", label: "Small (10-299)", order: 1, aliases: [] },
  { value: "mid", label: "Mid (300-3,000)", order: 2, aliases: [] },
  { value: "large", label: "Large (3,001-10,000)", order: 3, aliases: [] },
  { value: "enterprise", label: "Enterprise (>10,000)", order: 4, aliases: [] },
  { value: "unknown", label: "Unknown", order: 5, aliases: [] },
];

export const COMPANY_SIZE_BAND_DEFAULT = null;

const COMPANY_SIZE_BAND_INDEX = buildIndex(COMPANY_SIZE_BANDS);

/** Canonical company_size_band values, in display order. */
export const COMPANY_SIZE_BAND_VALUES: readonly string[] =
  COMPANY_SIZE_BANDS.map((term) => term.value);

/**
 * Resolve a company_size_band value or retired alias to its canonical value.
 * Returns null when the input matches no known term.
 */
export function canonicalCompanySizeBand(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return COMPANY_SIZE_BAND_INDEX.get(value.trim().toLowerCase()) ?? null;
}

/**
 * True when the stage represents an established relationship. Used to pick the
 * relationship-strength label for notes the MCP tools generate. Accepts aliases.
 */
export function isWarmStage(value: string | null | undefined): boolean {
  const canonical = canonicalConnectionPipelineStage(value);
  return CONNECTION_PIPELINE_STAGES.some(
    (term) => term.value === canonical && term.warm === true,
  );
}

/** Selects a note-status tier by rank so call sites never hard-code "A"/"B"/"C". */
function noteStatusByRank(rank: number): string {
  return (
    NOTE_STATUSES.find((term) => term.order === rank)?.value ??
    NOTE_STATUS_DEFAULT
  );
}

export const NOTE_STATUS_WEAK = noteStatusByRank(0);
export const NOTE_STATUS_MEDIUM = noteStatusByRank(1);
export const NOTE_STATUS_STRONG = noteStatusByRank(2);

/**
 * Relationship-strength label for a note the MCP tools auto-generate from a
 * stage change: the middle tier for an established relationship, the weakest
 * tier otherwise.
 */
export function noteStatusForStage(stage: string | null | undefined): string {
  return isWarmStage(stage) ? NOTE_STATUS_MEDIUM : NOTE_STATUS_WEAK;
}
